package com.evoapps.evoshift.domain.usecase.hiring

import com.evoapps.evoshift.domain.model.CandidateMail
import com.evoapps.evoshift.domain.model.Interview
import com.evoapps.evoshift.domain.model.InterviewRequest
import com.evoapps.evoshift.domain.model.InterviewResult
import com.evoapps.evoshift.domain.model.InvalidInterviewException
import com.evoapps.evoshift.domain.model.InvalidInterviewException.Problem
import com.evoapps.evoshift.domain.model.JobApplication
import com.evoapps.evoshift.domain.repository.HiringRepository
import com.evoapps.evoshift.domain.repository.InterviewRepository
import kotlinx.datetime.LocalDateTime

/**
 * SH-210: schedules an interview with an applicant, or moves one: a start ("YYYY-MM-DDTHH:mm",
 * the workplace's time), a length of 15 minutes to 8 hours, a place or an online link (http or
 * https), an optional note and up to 10 participants from the team; paid or not. The server
 * stores it, warns about clashes (never refuses them) and e-mails the candidate the calendar
 * invitation.
 */
class ScheduleInterviewUseCase(private val repository: InterviewRepository) {
    suspend operator fun invoke(request: InterviewRequest): Result<InterviewResult> = runCatching {
        repository.schedule(validated(request)).getOrThrow()
    }

    companion object {
        /** [request] trimmed, or why it cannot be sent. */
        fun validated(request: InterviewRequest): InterviewRequest {
            val startsAt = request.startsAt.trim().take(16)
            if (startsAt.length != 16 || runCatching { LocalDateTime.parse(startsAt) }.isFailure) {
                throw InvalidInterviewException(Problem.TIME_INVALID)
            }
            if (request.durationMinutes !in Interview.MIN_MINUTES..Interview.MAX_MINUTES) {
                throw InvalidInterviewException(Problem.LENGTH_INVALID)
            }
            val location = request.location?.trim()?.takeIf { it.isNotEmpty() }
            val url = request.onlineUrl?.trim()?.takeIf { it.isNotEmpty() }
            if (location == null && url == null) throw InvalidInterviewException(Problem.PLACE_REQUIRED)
            if (url != null && !(url.startsWith("https://") || url.startsWith("http://")) || url?.any { it.isWhitespace() } == true) {
                throw InvalidInterviewException(Problem.LINK_INVALID)
            }
            val note = request.note?.trim()?.takeIf { it.isNotEmpty() }
            if ((location?.length ?: 0) > Interview.MAX_LOCATION || (url?.length ?: 0) > Interview.MAX_URL ||
                (note?.length ?: 0) > Interview.MAX_NOTE
            ) throw InvalidInterviewException(Problem.TOO_LONG)
            val participants = request.participantIds.filter { it.isNotBlank() }.distinct()
            if (participants.size > Interview.MAX_PARTICIPANTS) throw InvalidInterviewException(Problem.PARTICIPANTS_TOO_MANY)
            return request.copy(startsAt = startsAt, location = location, onlineUrl = url, note = note, participantIds = participants)
        }
    }
}

/** SH-210: cancels an interview; the candidate gets the calendar cancellation when [notify]. */
class CancelInterviewUseCase(private val repository: InterviewRepository) {
    suspend operator fun invoke(interview: Interview, notify: Boolean): Result<CandidateMail> =
        if (interview.isCancelled) Result.success(CandidateMail.NOT_REQUESTED) else repository.cancel(interview.id, notify)
}

/**
 * SH-210: rejects an applicant, optionally with the post's rejection e-mail (the server sends it
 * once, and only to an applicant who gave an e-mail address). The retention runs from the decision.
 */
class RejectApplicantUseCase(private val repository: HiringRepository) {
    suspend operator fun invoke(application: JobApplication, sendMail: Boolean, managerNote: String?): Result<CandidateMail> {
        // Null keeps the note; blank clears it.
        val note = managerNote?.trim()?.take(JobApplication.MAX_MANAGER_NOTE)
        // No address, no e-mail (the server says the same; nothing is asked of it then).
        val mail = sendMail && application.email != null
        return repository.reject(application.postId, application.id, mail, note).map { answer ->
            if (sendMail && application.email == null) CandidateMail.NO_EMAIL else answer
        }
    }
}
